import React, { useEffect, useState } from "react";
import api from "../api";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const GOLD = "#D4AF37";

const ManageProducts = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [sortOption, setSortOption] = useState("");
  const [entriesToShow, setEntriesToShow] = useState(10);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [stockFilter, setStockFilter] = useState("");
  const [viewProduct, setViewProduct] = useState(null);

  // =========================
  // FORM DATA
  // =========================
  const [formData, setFormData] = useState({
    name: "",
    article_number: "",
    hsn: "",
    mrp_price: "",
    price: "",
    wholesale_price: "",
    description: "",
    specification: "",
    manufacturing_details: "",
    category_id: "",
    subcategory_id: "",
    qty: "",
    gst: "",
    video_link: "",
    return_days: "",
    product_type: "none",
    status: 1,

    image: null,
    image2: null,
    image3: null,
    image4: null,
  });

  // =========================
  // MAIN IMAGE PREVIEWS
  // =========================
  const [previews, setPreviews] = useState({
    image: null,
    image2: null,
    image3: null,
    image4: null,
  });

  // =========================
  // COLORS
  // =========================
  const [colors, setColors] = useState([]);

  // =========================
  // FETCH DATA
  // =========================
  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  useEffect(() => {
    if (formData.category_id) {
      fetchSubcategories(formData.category_id);
    } else {
      setSubcategories([]);
    }
  }, [formData.category_id]);

  // =========================
  // FETCH PRODUCTS
  // =========================
  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem("token");

      const res = await api.get("products", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setProducts(res.data.data || []);
    } catch (err) {
      console.error("Error fetching products:", err);
      toast.error("Failed to load products");
    }
  };

  // =========================
  // FETCH CATEGORIES
  // =========================
  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem("token");

      const res = await api.get("categories", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setCategories(res.data.data || []);
    } catch (err) {
      console.error("Error fetching categories:", err);
    }
  };

  // =========================
  // FETCH SUBCATEGORIES
  // =========================
  const fetchSubcategories = async (categoryId) => {
    try {
      const token = localStorage.getItem("token");

      const res = await api.get("subcategories", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const allSubcategories = res.data.data || [];

      const filtered = allSubcategories.filter(
        (sub) => Number(sub.category_id) === Number(categoryId)
      );

      setSubcategories(filtered);
    } catch (err) {
      console.error("Error fetching subcategories:", err);
    }
  };

  // =========================
  // OPEN EDIT MODAL
  // =========================
  const handleEdit = (product) => {
    setEditingProduct(product);

    setFormData({
      name: product.name || "",
      article_number: product.article_number || "",
      hsn: product.hsn || "",
      mrp_price: product.mrp_price || "",
      price: product.price || "",
      wholesale_price: product.wholesale_price || "",
      description: product.description || "",
      specification: product.specification || "",
      manufacturing_details: product.manufacturing_details || "",
      category_id: product.category_id || "",
      subcategory_id: product.subcategory_id || "",
      qty: product.qty ?? "",
      gst: product.gst ?? "",
      video_link: product.video_link || "",
      return_days: product.return_days ?? "",
      product_type: product.product_type || "none",

      status: Number(product.status) === 1 ? 1 : 0,

      image: null,
      image2: null,
      image3: null,
      image4: null,
    });

    setPreviews({
      image: product.image
        ? `${process.env.REACT_APP_API_URL}/public/${product.image}`
        : null,

      image2: product.image2
        ? `${process.env.REACT_APP_API_URL}/public/${product.image2}`
        : null,

      image3: product.image3
        ? `${process.env.REACT_APP_API_URL}/public/${product.image3}`
        : null,

      image4: product.image4
        ? `${process.env.REACT_APP_API_URL}/public/${product.image4}`
        : null,
    });

    setColors(
      product.colors?.map((color) => ({
        id: color.id,
        name: color.color_name || "",
        code: color.color_code || "",
        images: [],

        existingImages:
          color.images?.map((img) => ({
            id: img.id,
            image_path: img.image_path,
          })) || [],

        sizes:
          color.sizes?.map((size) => ({
            id: size.id,
            size: size.size || "",
            qty: size.qty ?? 0,
          })) || [],
      })) || []
    );
  };

  // =========================
  // MAIN IMAGE CHANGE
  // =========================
  const handleImageChange = (e, key) => {
    const file = e.target.files?.[0];

    if (!file) return;

    setFormData((prev) => ({
      ...prev,
      [key]: file,
    }));

    setPreviews((prev) => ({
      ...prev,
      [key]: URL.createObjectURL(file),
    }));
  };

  // =========================
  // UPDATE PRODUCT
  // =========================
  const handleUpdate = async (e) => {
    e.preventDefault();

    if (!editingProduct) return;

    const token = localStorage.getItem("token");

    const fd = new FormData();

    fd.append("name", formData.name);
    fd.append("article_number", formData.article_number);
    fd.append("hsn", formData.hsn);
    fd.append("mrp_price", formData.mrp_price);
    fd.append("price", formData.price);

    if (formData.wholesale_price !== "") {
      fd.append("wholesale_price", formData.wholesale_price);
    }

    fd.append("description", formData.description || "");
    fd.append("specification", formData.specification || "");

    fd.append(
      "manufacturing_details",
      formData.manufacturing_details || ""
    );

    if (formData.category_id) {
      fd.append("category_id", formData.category_id);
    }

    if (formData.subcategory_id) {
      fd.append("subcategory_id", formData.subcategory_id);
    }

    if (formData.qty !== "") {
      fd.append("qty", formData.qty);
    }

    if (formData.gst !== "") {
      fd.append("gst", formData.gst);
    }

    if (formData.video_link) {
      fd.append("video_link", formData.video_link);
    }

    if (formData.return_days !== "") {
      fd.append("return_days", formData.return_days);
    }

    fd.append("product_type", formData.product_type || "none");
    fd.append("status", formData.status ? 1 : 0);

    ["image", "image2", "image3", "image4"].forEach((key) => {
      if (formData[key] instanceof File) {
        fd.append(key, formData[key]);
      }
    });

    // =========================
    // COLORS
    // =========================
    colors.forEach((color, colorIndex) => {
      if (color.id) {
        fd.append(
          `colors[${colorIndex}][id]`,
          color.id
        );
      }

      fd.append(
        `colors[${colorIndex}][name]`,
        color.name || ""
      );

      fd.append(
        `colors[${colorIndex}][code]`,
        color.code || ""
      );

      if (color.images?.length > 0) {
        color.images.forEach((image, imageIndex) => {
          fd.append(
            `colors[${colorIndex}][images][${imageIndex}]`,
            image
          );
        });
      }

      color.sizes?.forEach((size, sizeIndex) => {
        if (size.id) {
          fd.append(
            `colors[${colorIndex}][sizes][${sizeIndex}][id]`,
            size.id
          );
        }

        fd.append(
          `colors[${colorIndex}][sizes][${sizeIndex}][size]`,
          size.size || ""
        );

        fd.append(
          `colors[${colorIndex}][sizes][${sizeIndex}][qty]`,
          size.qty ?? 0
        );
      });
    });

    try {
      const response = await api.post(
        `update-product/${editingProduct.id}`,
        fd,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.status) {
        toast.success(
          response.data.message ||
            "Product updated successfully!"
        );

        closeEditModal();
        fetchProducts();
      }
    } catch (err) {
      console.error("Update product error:", err);

      if (err.response?.status === 422) {
        const errors = err.response?.data?.errors || {};

        Object.values(errors).forEach((messages) => {
          if (Array.isArray(messages)) {
            messages.forEach((message) => {
              toast.error(message);
            });
          }
        });
      } else {
        toast.error(
          err.response?.data?.message ||
            "Failed to update product"
        );
      }
    }
  };

  // =========================
  // RESET / CLOSE EDIT
  // =========================
  const closeEditModal = () => {
    setEditingProduct(null);
    setColors([]);

    setFormData({
      name: "",
      article_number: "",
      hsn: "",
      mrp_price: "",
      price: "",
      wholesale_price: "",
      description: "",
      specification: "",
      manufacturing_details: "",
      category_id: "",
      subcategory_id: "",
      qty: "",
      gst: "",
      video_link: "",
      return_days: "",
      product_type: "none",
      status: 1,
      image: null,
      image2: null,
      image3: null,
      image4: null,
    });

    setPreviews({
      image: null,
      image2: null,
      image3: null,
      image4: null,
    });
  };

  // =========================
  // ADD NEW COLOR
  // =========================
  const addColor = () => {
    setColors((prev) => [
      ...prev,
      {
        id: null,
        name: "",
        code: "",
        images: [],
        existingImages: [],
        sizes: [
          {
            id: null,
            size: "",
            qty: "",
          },
        ],
      },
    ]);
  };

  // =========================
  // COLOR FIELD CHANGE
  // =========================
  const handleColorChange = (index, field, value) => {
    setColors((prev) => {
      const updated = [...prev];

      updated[index] = {
        ...updated[index],
        [field]: value,
      };

      return updated;
    });
  };

  // =========================
  // COLOR IMAGE CHANGE
  // =========================
  const handleColorImageChange = (e, colorIndex) => {
    const files = Array.from(e.target.files || []);

    if (!files.length) return;

    setColors((prev) => {
      const updated = [...prev];

      updated[colorIndex] = {
        ...updated[colorIndex],
        images: [
          ...(updated[colorIndex].images || []),
          ...files,
        ],
      };

      return updated;
    });
  };

  // =========================
  // ADD SIZE
  // =========================
  const addColorSize = (colorIndex) => {
    setColors((prev) => {
      const updated = [...prev];

      updated[colorIndex] = {
        ...updated[colorIndex],
        sizes: [
          ...(updated[colorIndex].sizes || []),
          {
            id: null,
            size: "",
            qty: "",
          },
        ],
      };

      return updated;
    });
  };

  // =========================
  // SIZE CHANGE
  // =========================
  const handleColorSizeChange = (
    colorIndex,
    sizeIndex,
    field,
    value
  ) => {
    setColors((prev) => {
      const updated = [...prev];

      const sizes = [...updated[colorIndex].sizes];

      sizes[sizeIndex] = {
        ...sizes[sizeIndex],
        [field]: value,
      };

      updated[colorIndex] = {
        ...updated[colorIndex],
        sizes,
      };

      return updated;
    });
  };

  // =========================
  // DELETE PRODUCT
  // =========================
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this product?")) return;

    try {
      const token = localStorage.getItem("token");

      await api.delete(`delete-product/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      toast.success("Product deleted!");
      fetchProducts();
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete");
    }
  };

  // =========================
  // FILTERS
  // =========================
  const filteredProducts = products
    .filter((product) =>
      product.name
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase())
    )
    .filter(
      (product) =>
        !categoryFilter ||
        product.category_id == categoryFilter
    )
    .filter((product) => {
      if (!stockFilter) return true;

      return stockFilter === "instock"
        ? product.qty > 0
        : product.qty === 0;
    });

  // =========================
  // SORT
  // =========================
  const sortedProducts = [...filteredProducts].sort(
    (a, b) => {
      if (sortOption === "name") {
        return (a.name || "").localeCompare(
          b.name || ""
        );
      }

      if (sortOption === "price_low") {
        return Number(a.price) - Number(b.price);
      }

      if (sortOption === "price_high") {
        return Number(b.price) - Number(a.price);
      }

      if (sortOption === "qty") {
        return Number(b.qty) - Number(a.qty);
      }

      if (sortOption === "date") {
        return (
          new Date(b.created_at) -
          new Date(a.created_at)
        );
      }

      return 0;
    }
  );

  // =========================
  // PAGINATION
  // =========================
  const totalPages = Math.ceil(
    sortedProducts.length / entriesToShow
  );

  const startIndex =
    (page - 1) * entriesToShow;

  const paginatedProducts =
    sortedProducts.slice(
      startIndex,
      startIndex + entriesToShow
    );

  return (
    <div className="min-h-screen bg-black text-white px-3 sm:px-5 lg:px-8 py-5 sm:py-7">

      <div className="max-w-[1600px] mx-auto">

        {/* =====================================================
            HEADER
        ===================================================== */}
        <div className="relative overflow-hidden bg-black border border-[#D4AF37]/40 rounded-2xl sm:rounded-3xl shadow-2xl mb-6 sm:mb-8">

          {/* Golden Glow */}
          <div className="absolute -right-24 -top-24 w-72 h-72 rounded-full bg-[#D4AF37]/20 blur-3xl" />
          <div className="absolute right-20 bottom-0 w-40 h-40 rounded-full bg-[#D4AF37]/10 blur-3xl" />

          <div className="relative z-10 px-5 sm:px-7 lg:px-10 py-6 sm:py-8">

            <div className="flex items-center gap-4">

              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-[#D4AF37] text-black flex items-center justify-center font-black text-xl sm:text-2xl shadow-lg shadow-[#D4AF37]/20">
                P
              </div>

              <div>
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">
                  Manage Products
                </h1>

                <p className="text-gray-400 text-sm sm:text-base mt-1">
                  Manage product details, images, colors, sizes and stock.
                </p>
              </div>

            </div>

          </div>
        </div>

        {/* =====================================================
            MAIN CARD
        ===================================================== */}
        <div className="bg-[#0b0b0b] border border-[#D4AF37]/30 rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden">

          {/* =====================================================
              FILTER HEADER
          ===================================================== */}
          <div className="p-4 sm:p-6 border-b border-[#D4AF37]/20">

            <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-5">

              <div>
                <div className="flex items-center gap-3">

                  <h2 className="text-lg sm:text-xl font-bold text-white">
                    Product List
                  </h2>

                  <span className="min-w-[34px] h-7 px-2 rounded-full bg-[#D4AF37] text-black text-xs sm:text-sm font-bold flex items-center justify-center">
                    {sortedProducts.length}
                  </span>

                </div>

                <p className="text-gray-500 text-sm mt-1">
                  View, edit and manage all products
                </p>
              </div>

              {/* Search */}
              <div className="relative w-full xl:w-96">

                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#D4AF37]">
                  🔍
                </span>

                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setPage(1);
                  }}
                  className="
                    w-full
                    bg-black
                    border border-gray-700
                    text-white
                    placeholder-gray-500
                    rounded-xl sm:rounded-2xl
                    pl-11 pr-4 py-3
                    outline-none
                    transition
                    focus:border-[#D4AF37]
                    focus:ring-2
                    focus:ring-[#D4AF37]/20
                  "
                />

              </div>

            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-3 mt-5">

              <select
                value={entriesToShow}
                onChange={(e) => {
                  setEntriesToShow(Number(e.target.value));
                  setPage(1);
                }}
                className="
                  bg-black
                  text-white
                  border border-gray-700
                  px-4 py-2.5
                  rounded-xl
                  outline-none
                  focus:border-[#D4AF37]
                "
              >
                <option value="10">Show 10</option>
                <option value="20">Show 20</option>
                <option value="50">Show 50</option>
              </select>

              <select
                value={categoryFilter}
                onChange={(e) => {
                  setCategoryFilter(e.target.value);
                  setPage(1);
                }}
                className="
                  bg-black
                  text-white
                  border border-gray-700
                  px-4 py-2.5
                  rounded-xl
                  outline-none
                  focus:border-[#D4AF37]
                "
              >
                <option value="">All Categories</option>

                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>

              <select
                value={stockFilter}
                onChange={(e) => {
                  setStockFilter(e.target.value);
                  setPage(1);
                }}
                className="
                  bg-black
                  text-white
                  border border-gray-700
                  px-4 py-2.5
                  rounded-xl
                  outline-none
                  focus:border-[#D4AF37]
                "
              >
                <option value="">All Status</option>
                <option value="instock">In Stock</option>
                <option value="outstock">Out of Stock</option>
              </select>

              <select
                value={sortOption}
                onChange={(e) =>
                  setSortOption(e.target.value)
                }
                className="
                  bg-black
                  text-white
                  border border-gray-700
                  px-4 py-2.5
                  rounded-xl
                  outline-none
                  focus:border-[#D4AF37]
                "
              >
                <option value="">Sort By</option>
                <option value="name">Name (A–Z)</option>
                <option value="price_low">
                  Price Low → High
                </option>
                <option value="price_high">
                  Price High → Low
                </option>
                <option value="qty">
                  Quantity High → Low
                </option>
                <option value="date">
                  Newest First
                </option>
              </select>

            </div>

          </div>

          {/* =====================================================
              RESPONSIVE TABLE
          ===================================================== */}
          <div className="w-full overflow-x-auto overscroll-x-contain">

            <table className="w-full min-w-[1000px] border-collapse">

              <thead className="bg-black border-b-2 border-[#D4AF37]">

                <tr>

                  <th className="px-4 sm:px-6 py-4 text-left text-xs sm:text-sm font-semibold text-[#D4AF37] whitespace-nowrap">
                    Product
                  </th>

                  <th className="px-4 sm:px-6 py-4 text-left text-xs sm:text-sm font-semibold text-[#D4AF37] whitespace-nowrap">
                    ID
                  </th>

                  <th className="px-4 sm:px-6 py-4 text-left text-xs sm:text-sm font-semibold text-[#D4AF37] whitespace-nowrap">
                    Price
                  </th>

                  <th className="px-4 sm:px-6 py-4 text-left text-xs sm:text-sm font-semibold text-[#D4AF37] whitespace-nowrap">
                    Qty
                  </th>

                  <th className="px-4 sm:px-6 py-4 text-left text-xs sm:text-sm font-semibold text-[#D4AF37] whitespace-nowrap">
                    Stock
                  </th>

                  <th className="px-4 sm:px-6 py-4 text-center text-xs sm:text-sm font-semibold text-[#D4AF37] whitespace-nowrap">
                    Actions
                  </th>

                </tr>

              </thead>

              <tbody className="divide-y divide-gray-800">

                {paginatedProducts.length > 0 ? (

                  paginatedProducts.map((prod) => (

                    <tr
                      key={prod.id}
                      className="
                        group
                        bg-[#0b0b0b]
                        hover:bg-[#D4AF37]/10
                        transition-colors
                      "
                    >

                      {/* Product */}
                      <td className="px-4 sm:px-6 py-4">

                        <div className="flex items-center gap-3">

                          <div className="w-12 h-12 rounded-xl overflow-hidden border border-gray-700 bg-black flex-shrink-0">

                            <img
                              src={
                                prod.image
                                  ? `${process.env.REACT_APP_API_URL}/public/${prod.image}`
                                  : "/placeholder.jpg"
                              }
                              className="w-full h-full object-cover"
                              alt={prod.name}
                            />

                          </div>

                          <div className="min-w-0">

                            <div className="font-semibold text-white truncate max-w-[280px]">
                              {prod.name}
                            </div>

                            <div className="text-xs text-gray-500 mt-1">
                              {prod.article_number || "No Article Number"}
                            </div>

                          </div>

                        </div>

                      </td>

                      {/* ID */}
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap">

                        <span className="inline-flex px-2.5 py-1 rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] text-xs font-bold">
                          #{prod.id}
                        </span>

                      </td>

                      {/* Price */}
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap">

                        <div className="font-bold text-[#D4AF37]">
                          ₹{prod.price}
                        </div>

                        {prod.mrp_price && (
                          <div className="text-xs text-gray-500 line-through mt-1">
                            ₹{prod.mrp_price}
                          </div>
                        )}

                      </td>

                      {/* Qty */}
                      <td className="px-4 sm:px-6 py-4 text-gray-300 whitespace-nowrap font-medium">
                        {prod.qty}
                      </td>

                      {/* Stock */}
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap">

                        {prod.qty > 0 ? (

                          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] text-xs font-semibold">

                            <span className="w-2 h-2 rounded-full bg-[#D4AF37]" />

                            In Stock

                          </span>

                        ) : (

                          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-500/10 border border-red-500/30 text-red-400 text-xs font-semibold">

                            <span className="w-2 h-2 rounded-full bg-red-500" />

                            Out of Stock

                          </span>

                        )}

                      </td>

                      {/* Actions */}
                      <td className="px-4 sm:px-6 py-4">

                        <div className="flex justify-center items-center gap-2">

                          {/* View */}
                          <button
                            onClick={() =>
                              setViewProduct(prod)
                            }
                            title="View Product"
                            className="
                              w-10 h-10
                              rounded-xl
                              bg-gray-900
                              border border-gray-700
                              text-gray-300
                              hover:text-[#D4AF37]
                              hover:border-[#D4AF37]
                              hover:bg-[#D4AF37]/10
                              transition-all
                              active:scale-95
                            "
                          >
                            👁
                          </button>

                          {/* Edit */}
                          <button
                            onClick={() =>
                              handleEdit(prod)
                            }
                            title="Edit Product"
                            className="
                              w-10 h-10
                              rounded-xl
                              bg-[#D4AF37]/10
                              border border-[#D4AF37]/30
                              text-[#D4AF37]
                              hover:bg-[#D4AF37]
                              hover:text-black
                              transition-all
                              active:scale-95
                            "
                          >
                            ✏️
                          </button>

                          {/* Delete */}
                          <button
                            onClick={() =>
                              handleDelete(prod.id)
                            }
                            title="Delete Product"
                            className="
                              w-10 h-10
                              rounded-xl
                              bg-red-500/10
                              border border-red-500/20
                              text-red-400
                              hover:bg-red-500
                              hover:text-white
                              transition-all
                              active:scale-95
                            "
                          >
                            🗑
                          </button>

                        </div>

                      </td>

                    </tr>

                  ))

                ) : (

                  <tr>

                    <td
                      colSpan="6"
                      className="py-16 text-center"
                    >

                      <div className="flex flex-col items-center">

                        <div className="w-16 h-16 rounded-2xl bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center text-2xl mb-4">
                          📦
                        </div>

                        <h3 className="text-lg font-semibold text-white">
                          No Products Found
                        </h3>

                        <p className="text-sm text-gray-500 mt-1">
                          No products match your current filters.
                        </p>

                      </div>

                    </td>

                  </tr>

                )}

              </tbody>

            </table>

          </div>

          {/* Mobile Scroll Hint */}
          <div className="lg:hidden px-4 py-3 bg-black border-t border-gray-800 text-center text-xs text-gray-500">
            ← Swipe horizontally to view all product details →
          </div>

          {/* =====================================================
              PAGINATION
          ===================================================== */}
          <div className="px-4 sm:px-6 py-5 border-t border-[#D4AF37]/20 flex flex-col sm:flex-row justify-between items-center gap-4">

            <span className="text-sm text-gray-500 text-center sm:text-left">

              Showing{" "}
              <span className="text-[#D4AF37] font-semibold">
                {sortedProducts.length === 0
                  ? 0
                  : startIndex + 1}
              </span>{" "}
              to{" "}
              <span className="text-[#D4AF37] font-semibold">
                {Math.min(
                  startIndex + entriesToShow,
                  sortedProducts.length
                )}
              </span>{" "}
              of{" "}
              <span className="text-white font-semibold">
                {sortedProducts.length}
              </span>{" "}
              products

            </span>

            <div className="flex items-center gap-2 sm:gap-3">

              <button
                disabled={page === 1}
                onClick={() =>
                  setPage((prev) => prev - 1)
                }
                className="
                  px-4 py-2
                  border border-gray-700
                  rounded-xl
                  text-gray-300
                  hover:border-[#D4AF37]
                  hover:text-[#D4AF37]
                  disabled:opacity-30
                  disabled:cursor-not-allowed
                  transition
                "
              >
                Prev
              </button>

              <span className="px-4 py-2 bg-[#D4AF37] text-black rounded-xl font-bold text-sm">
                {page} / {totalPages || 1}
              </span>

              <button
                disabled={
                  page === totalPages ||
                  totalPages === 0
                }
                onClick={() =>
                  setPage((prev) => prev + 1)
                }
                className="
                  px-4 py-2
                  border border-gray-700
                  rounded-xl
                  text-gray-300
                  hover:border-[#D4AF37]
                  hover:text-[#D4AF37]
                  disabled:opacity-30
                  disabled:cursor-not-allowed
                  transition
                "
              >
                Next
              </button>

            </div>

          </div>

        </div>

        {/* =====================================================
            EDIT PRODUCT MODAL
        ===================================================== */}
        {editingProduct && (

          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-3 sm:p-4">

            <div className="bg-[#0b0b0b] border border-[#D4AF37]/40 text-white w-full max-w-6xl rounded-2xl sm:rounded-3xl shadow-2xl max-h-[95vh] overflow-hidden">

              {/* Modal Header */}
              <div className="bg-black border-b border-[#D4AF37]/40 px-5 sm:px-8 py-5 flex items-center justify-between">

                <div>

                  <div className="flex items-center gap-3">

                    <div className="w-10 h-10 rounded-xl bg-[#D4AF37] text-black flex items-center justify-center font-bold">
                      P
                    </div>

                    <div>

                      <h3 className="text-xl sm:text-2xl font-bold text-white">
                        Edit Product
                      </h3>

                      <p className="text-[#D4AF37] text-sm mt-1">
                        Product ID: #{editingProduct.id}
                      </p>

                    </div>

                  </div>

                </div>

                <button
                  type="button"
                  onClick={closeEditModal}
                  className="
                    w-10 h-10
                    rounded-xl
                    bg-gray-900
                    border border-gray-700
                    text-gray-300
                    hover:text-[#D4AF37]
                    hover:border-[#D4AF37]
                    transition
                  "
                >
                  ✕
                </button>

              </div>

              {/* Form */}
              <form
                onSubmit={handleUpdate}
                className="p-5 sm:p-7 lg:p-8 space-y-8 overflow-y-auto max-h-[calc(95vh-90px)]"
              >

                {/* =====================================================
                    BASIC INFORMATION
                ===================================================== */}
                <div>

                  <div className="mb-5">

                    <h3 className="text-xl font-bold text-white">
                      Basic Product Information
                    </h3>

                    <p className="text-sm text-gray-500 mt-1">
                      Update the basic information of your product.
                    </p>

                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6">

                    {[
                      {
                        label: "Product Name *",
                        key: "name",
                        type: "text",
                        placeholder: "Enter product name",
                        required: true,
                      },
                      {
                        label: "Article Number *",
                        key: "article_number",
                        type: "text",
                        placeholder: "Enter article number",
                        required: true,
                      },
                      {
                        label: "HSN Code *",
                        key: "hsn",
                        type: "text",
                        placeholder: "Enter HSN code",
                        required: true,
                      },
                    ].map((field) => (

                      <div key={field.key}>

                        <label className="block text-sm font-semibold text-gray-300 mb-2">
                          {field.label}
                        </label>

                        <input
                          type={field.type}
                          value={formData[field.key]}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              [field.key]:
                                e.target.value,
                            })
                          }
                          className="
                            w-full
                            bg-black
                            border border-gray-700
                            text-white
                            rounded-xl
                            px-4 py-3
                            outline-none
                            placeholder-gray-600
                            focus:border-[#D4AF37]
                            focus:ring-2
                            focus:ring-[#D4AF37]/20
                          "
                          placeholder={field.placeholder}
                          required={field.required}
                        />

                      </div>

                    ))}

                    {/* MRP */}
                    <div>

                      <label className="block text-sm font-semibold text-gray-300 mb-2">
                        MRP Price *
                      </label>

                      <div className="relative">

                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#D4AF37]">
                          ₹
                        </span>

                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={formData.mrp_price}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              mrp_price:
                                e.target.value,
                            })
                          }
                          className="
                            w-full bg-black
                            border border-gray-700
                            text-white
                            rounded-xl
                            pl-9 pr-4 py-3
                            outline-none
                            focus:border-[#D4AF37]
                            focus:ring-2
                            focus:ring-[#D4AF37]/20
                          "
                          required
                        />

                      </div>

                    </div>

                    {/* Price */}
                    <div>

                      <label className="block text-sm font-semibold text-gray-300 mb-2">
                        Discounted Price *
                      </label>

                      <div className="relative">

                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#D4AF37]">
                          ₹
                        </span>

                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={formData.price}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              price: e.target.value,
                            })
                          }
                          className="
                            w-full bg-black
                            border border-gray-700
                            text-white
                            rounded-xl
                            pl-9 pr-4 py-3
                            outline-none
                            focus:border-[#D4AF37]
                            focus:ring-2
                            focus:ring-[#D4AF37]/20
                          "
                          required
                        />

                      </div>

                    </div>

                    {/* Wholesale */}
                    <div>

                      <label className="block text-sm font-semibold text-gray-300 mb-2">
                        Wholesale Price
                      </label>

                      <div className="relative">

                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#D4AF37]">
                          ₹
                        </span>

                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={formData.wholesale_price}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              wholesale_price:
                                e.target.value,
                            })
                          }
                          className="
                            w-full bg-black
                            border border-gray-700
                            text-white
                            rounded-xl
                            pl-9 pr-4 py-3
                            outline-none
                            focus:border-[#D4AF37]
                            focus:ring-2
                            focus:ring-[#D4AF37]/20
                          "
                          placeholder="Enter wholesale price"
                        />

                      </div>

                    </div>

                    {/* Quantity */}
                    <div>

                      <label className="block text-sm font-semibold text-gray-300 mb-2">
                        Product Quantity
                      </label>

                      <input
                        type="number"
                        min="0"
                        value={formData.qty}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            qty: e.target.value,
                          })
                        }
                        className="
                          w-full bg-black
                          border border-gray-700
                          text-white
                          rounded-xl
                          px-4 py-3
                          outline-none
                          focus:border-[#D4AF37]
                          focus:ring-2
                          focus:ring-[#D4AF37]/20
                        "
                        placeholder="Enter available quantity"
                      />

                    </div>

                    {/* GST */}
                    <div>

                      <label className="block text-sm font-semibold text-gray-300 mb-2">
                        GST (%)
                      </label>

                      <div className="relative">

                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={formData.gst}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              gst: e.target.value,
                            })
                          }
                          className="
                            w-full bg-black
                            border border-gray-700
                            text-white
                            rounded-xl
                            px-4 py-3 pr-10
                            outline-none
                            focus:border-[#D4AF37]
                            focus:ring-2
                            focus:ring-[#D4AF37]/20
                          "
                          placeholder="Enter GST percentage"
                        />

                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[#D4AF37]">
                          %
                        </span>

                      </div>

                    </div>

                    {/* Return */}
                    <div>

                      <label className="block text-sm font-semibold text-gray-300 mb-2">
                        Return Period (Days)
                      </label>

                      <input
                        type="number"
                        min="0"
                        value={formData.return_days}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            return_days:
                              e.target.value,
                          })
                        }
                        className="
                          w-full bg-black
                          border border-gray-700
                          text-white
                          rounded-xl
                          px-4 py-3
                          outline-none
                          focus:border-[#D4AF37]
                          focus:ring-2
                          focus:ring-[#D4AF37]/20
                        "
                        placeholder="Example: 7"
                      />

                      <p className="text-xs text-gray-500 mt-1">
                        Enter 0 if this product is non-returnable.
                      </p>

                    </div>

                  </div>

                </div>

                {/* =====================================================
                    CATEGORY
                ===================================================== */}
                <div className="border-t border-gray-800 pt-8">

                  <div className="mb-5">

                    <h3 className="text-xl font-bold text-white">
                      Category & Product Type
                    </h3>

                    <p className="text-sm text-gray-500 mt-1">
                      Select category, subcategory and product type.
                    </p>

                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5">

                    {[
                      {
                        label: "Category",
                        value: formData.category_id,
                        onChange: (e) =>
                          setFormData({
                            ...formData,
                            category_id:
                              e.target.value,
                            subcategory_id: "",
                          }),
                        options: categories,
                        placeholder: "Select Category",
                      },
                      {
                        label: "Subcategory",
                        value: formData.subcategory_id,
                        onChange: (e) =>
                          setFormData({
                            ...formData,
                            subcategory_id:
                              e.target.value,
                          }),
                        options: subcategories,
                        placeholder: "Select Subcategory",
                      },
                    ].map((field, index) => (

                      <div key={index}>

                        <label className="block text-sm font-semibold text-gray-300 mb-2">
                          {field.label}
                        </label>

                        <select
                          value={field.value}
                          onChange={field.onChange}
                          className="
                            w-full bg-black
                            text-white
                            border border-gray-700
                            rounded-xl
                            px-4 py-3
                            outline-none
                            focus:border-[#D4AF37]
                            focus:ring-2
                            focus:ring-[#D4AF37]/20
                          "
                        >
                          <option value="">
                            {field.placeholder}
                          </option>

                          {field.options.map((item) => (
                            <option
                              key={item.id}
                              value={item.id}
                            >
                              {item.name}
                            </option>
                          ))}

                        </select>

                      </div>

                    ))}

                    {/* Product Type */}
                    <div>

                      <label className="block text-sm font-semibold text-gray-300 mb-2">
                        Product Type
                      </label>

                      <select
                        value={formData.product_type}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            product_type:
                              e.target.value,
                          })
                        }
                        className="
                          w-full bg-black
                          text-white
                          border border-gray-700
                          rounded-xl
                          px-4 py-3
                          outline-none
                          focus:border-[#D4AF37]
                          focus:ring-2
                          focus:ring-[#D4AF37]/20
                        "
                      >
                        <option value="none">
                          None
                        </option>
                        <option value="new">
                          New Arrival
                        </option>
                        <option value="special_offer">
                          Special Offer
                        </option>
                        <option value="hot_deal">
                          Hot Deal
                        </option>
                      </select>

                    </div>

                  </div>

                </div>

                {/* =====================================================
                    DESCRIPTION
                ===================================================== */}
                <div className="border-t border-gray-800 pt-8">

                  <div className="mb-5">

                    <h3 className="text-xl font-bold text-white">
                      Product Description
                    </h3>

                    <p className="text-sm text-gray-500 mt-1">
                      Update product description and details.
                    </p>

                  </div>

                  <div className="mb-6">

                    <label className="block text-sm font-semibold text-gray-300 mb-2">
                      Description
                    </label>

                    <div className="rounded-2xl overflow-hidden border border-gray-700 bg-white">

                      <ReactQuill
                        theme="snow"
                        value={formData.description}
                        onChange={(value) =>
                          setFormData({
                            ...formData,
                            description: value,
                          })
                        }
                      />

                    </div>

                  </div>

                  <div className="mb-6">

                    <label className="block text-sm font-semibold text-gray-300 mb-2">
                      Specification
                    </label>

                    <textarea
                      rows="5"
                      value={formData.specification}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          specification:
                            e.target.value,
                        })
                      }
                      className="
                        w-full bg-black
                        border border-gray-700
                        text-white
                        rounded-xl
                        px-4 py-3
                        outline-none
                        resize-none
                        focus:border-[#D4AF37]
                        focus:ring-2
                        focus:ring-[#D4AF37]/20
                      "
                      placeholder="Enter product specifications"
                    />

                  </div>

                  <div>

                    <label className="block text-sm font-semibold text-gray-300 mb-2">
                      Manufacturing Details
                    </label>

                    <textarea
                      rows="5"
                      value={formData.manufacturing_details}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          manufacturing_details:
                            e.target.value,
                        })
                      }
                      className="
                        w-full bg-black
                        border border-gray-700
                        text-white
                        rounded-xl
                        px-4 py-3
                        outline-none
                        resize-none
                        focus:border-[#D4AF37]
                        focus:ring-2
                        focus:ring-[#D4AF37]/20
                      "
                      placeholder="Enter manufacturing details"
                    />

                  </div>

                </div>

                {/* =====================================================
                    VIDEO
                ===================================================== */}
                <div className="border-t border-gray-800 pt-8">

                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    Product Video Link
                  </label>

                  <input
                    type="url"
                    value={formData.video_link}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        video_link:
                          e.target.value,
                      })
                    }
                    className="
                      w-full bg-black
                      border border-gray-700
                      text-white
                      rounded-xl
                      px-4 py-3
                      outline-none
                      focus:border-[#D4AF37]
                      focus:ring-2
                      focus:ring-[#D4AF37]/20
                    "
                    placeholder="https://www.youtube.com/..."
                  />

                  <p className="text-xs text-gray-500 mt-1">
                    Enter a valid video URL.
                  </p>

                </div>

                {/* =====================================================
                    MAIN IMAGES
                ===================================================== */}
                <div className="border-t border-gray-800 pt-8">

                  <div className="mb-5">

                    <h3 className="text-xl font-bold text-white">
                      Main Product Images
                    </h3>

                    <p className="text-sm text-gray-500 mt-1">
                      Select a new image only if you want to replace
                      the existing image.
                    </p>

                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-5">

                    {[
                      {
                        key: "image",
                        label: "Main Image",
                      },
                      {
                        key: "image2",
                        label: "Image 2",
                      },
                      {
                        key: "image3",
                        label: "Image 3",
                      },
                      {
                        key: "image4",
                        label: "Image 4",
                      },
                    ].map((item) => (

                      <div
                        key={item.key}
                        className="
                          border border-gray-700
                          rounded-2xl
                          p-4
                          bg-black
                        "
                      >

                        <label className="block text-sm font-semibold text-gray-300 mb-3">
                          {item.label}
                        </label>

                        <div className="w-full h-36 bg-[#0b0b0b] rounded-xl border border-gray-700 flex items-center justify-center overflow-hidden mb-3">

                          {previews[item.key] ? (

                            <img
                              src={previews[item.key]}
                              alt={item.label}
                              className="w-full h-full object-cover"
                            />

                          ) : (

                            <span className="text-sm text-gray-600">
                              No Image
                            </span>

                          )}

                        </div>

                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) =>
                            handleImageChange(
                              e,
                              item.key
                            )
                          }
                          className="w-full text-sm text-gray-400"
                        />

                      </div>

                    ))}

                  </div>

                </div>

                {/* =====================================================
                    COLORS & SIZES
                ===================================================== */}
                <div className="border-t border-gray-800 pt-8">

                  <div className="mb-5">

                    <h3 className="text-xl font-bold text-white">
                      Colors & Sizes
                    </h3>

                    <p className="text-sm text-gray-500 mt-1">
                      Manage product colors, color images and sizes.
                    </p>

                  </div>

                  <div className="border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-4 sm:p-6 rounded-3xl">

                    {colors.length === 0 && (

                      <div className="bg-black border border-dashed border-[#D4AF37]/40 rounded-2xl p-8 text-center mb-5">

                        <div className="text-3xl mb-3">
                          🎨
                        </div>

                        <p className="text-gray-400">
                          No colors added to this product.
                        </p>

                        <p className="text-sm text-gray-600 mt-1">
                          Click "Add New Color" below to add one.
                        </p>

                      </div>

                    )}

                    {colors.map((color, cIndex) => (

                      <div
                        key={cIndex}
                        className="
                          bg-[#0b0b0b]
                          p-4 sm:p-6
                          rounded-2xl
                          mb-6
                          border border-gray-700
                        "
                      >

                        {/* Color Header */}
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">

                          <h4 className="text-lg font-bold text-white">
                            Color {cIndex + 1}
                          </h4>

                          {color.id && (

                            <span className="text-xs bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] px-3 py-1 rounded-full">
                              Existing Color #{color.id}
                            </span>

                          )}

                        </div>

                        {/* Name + Code */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

                          <div>

                            <label className="block text-sm font-semibold text-gray-300 mb-2">
                              Color Name *
                            </label>

                            <input
                              type="text"
                              value={color.name}
                              onChange={(e) =>
                                handleColorChange(
                                  cIndex,
                                  "name",
                                  e.target.value
                                )
                              }
                              className="
                                w-full
                                bg-black
                                border border-gray-700
                                text-white
                                rounded-xl
                                px-4 py-3
                                outline-none
                                focus:border-[#D4AF37]
                                focus:ring-2
                                focus:ring-[#D4AF37]/20
                              "
                              placeholder="Example: Red"
                              required
                            />

                          </div>

                          <div>

                            <label className="block text-sm font-semibold text-gray-300 mb-2">
                              Color Code *
                            </label>

                            <div className="h-[50px] w-full border border-gray-700 rounded-xl flex items-center px-3 gap-3 bg-black">

                              <input
                                type="color"
                                value={
                                  color.code ||
                                  "#000000"
                                }
                                onChange={(e) =>
                                  handleColorChange(
                                    cIndex,
                                    "code",
                                    e.target.value
                                  )
                                }
                                className="w-9 h-9 p-0 border-0 rounded cursor-pointer bg-transparent"
                              />

                              <span className="text-sm text-[#D4AF37] font-mono">
                                {color.code ||
                                  "#000000"}
                              </span>

                            </div>

                          </div>

                        </div>

                        {/* Existing Images */}
                        {color.existingImages?.length > 0 && (

                          <div className="mt-6">

                            <label className="block text-sm font-semibold text-gray-300 mb-3">
                              Existing Color Images
                            </label>

                            <div className="flex flex-wrap gap-4">

                              {color.existingImages.map(
                                (image) => (

                                  <div
                                    key={image.id}
                                    className="w-24 h-24 rounded-xl overflow-hidden border border-gray-700 bg-black"
                                  >

                                    <img
                                      src={`${process.env.REACT_APP_API_URL}/public/${image.image_path}`}
                                      alt="Color"
                                      className="w-full h-full object-cover"
                                    />

                                  </div>

                                )
                              )}

                            </div>

                          </div>

                        )}

                        {/* New Color Images */}
                        <div className="mt-6">

                          <label className="block text-sm font-semibold text-gray-300 mb-2">
                            Add New Color Images
                          </label>

                          <input
                            type="file"
                            multiple
                            accept="image/*"
                            onChange={(e) =>
                              handleColorImageChange(
                                e,
                                cIndex
                              )
                            }
                            className="w-full text-sm text-gray-400"
                          />

                          {color.images?.length > 0 && (

                            <div className="flex flex-wrap gap-3 mt-4">

                              {color.images.map(
                                (image, imageIndex) => (

                                  <div
                                    key={imageIndex}
                                    className="relative"
                                  >

                                    <img
                                      src={URL.createObjectURL(
                                        image
                                      )}
                                      alt="New color"
                                      className="w-20 h-20 object-cover rounded-xl border border-[#D4AF37]/40"
                                    />

                                  </div>

                                )
                              )}

                            </div>

                          )}

                        </div>

                        {/* Sizes */}
                        <div className="mt-7 border-t border-gray-800 pt-6">

                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">

                            <div>

                              <h4 className="font-bold text-white">
                                Available Sizes
                              </h4>

                              <p className="text-xs text-gray-500 mt-1">
                                Set size and quantity for this color.
                              </p>

                            </div>

                            <button
                              type="button"
                              onClick={() =>
                                addColorSize(
                                  cIndex
                                )
                              }
                              className="
                                text-sm
                                font-semibold
                                text-[#D4AF37]
                                hover:text-white
                                transition
                              "
                            >
                              + Add Size
                            </button>

                          </div>

                          {color.sizes?.length === 0 && (

                            <div className="text-sm text-gray-500 bg-black border border-dashed border-gray-700 rounded-xl p-4 text-center">
                              No sizes added.
                            </div>

                          )}

                          {color.sizes?.map(
                            (size, sIndex) => (

                              <div
                                key={sIndex}
                                className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4"
                              >

                                <div>

                                  <label className="block text-xs font-semibold text-gray-400 mb-1">
                                    Size
                                  </label>

                                  <input
                                    type="text"
                                    value={size.size}
                                    onChange={(e) =>
                                      handleColorSizeChange(
                                        cIndex,
                                        sIndex,
                                        "size",
                                        e.target.value
                                      )
                                    }
                                    className="
                                      w-full bg-black
                                      border border-gray-700
                                      text-white
                                      rounded-xl
                                      px-4 py-3
                                      outline-none
                                      focus:border-[#D4AF37]
                                      focus:ring-2
                                      focus:ring-[#D4AF37]/20
                                    "
                                    placeholder="Example: M, L, XL"
                                  />

                                </div>

                                <div>

                                  <label className="block text-xs font-semibold text-gray-400 mb-1">
                                    Size Quantity
                                  </label>

                                  <input
                                    type="number"
                                    min="0"
                                    value={size.qty}
                                    onChange={(e) =>
                                      handleColorSizeChange(
                                        cIndex,
                                        sIndex,
                                        "qty",
                                        e.target.value
                                      )
                                    }
                                    className="
                                      w-full bg-black
                                      border border-gray-700
                                      text-white
                                      rounded-xl
                                      px-4 py-3
                                      outline-none
                                      focus:border-[#D4AF37]
                                      focus:ring-2
                                      focus:ring-[#D4AF37]/20
                                    "
                                    placeholder="Enter quantity"
                                  />

                                </div>

                              </div>

                            )
                          )}

                        </div>

                      </div>

                    ))}

                    {/* Add Color */}
                    <button
                      type="button"
                      onClick={addColor}
                      className="
                        w-full
                        py-4
                        border-2
                        border-dashed
                        border-[#D4AF37]/50
                        rounded-2xl
                        text-[#D4AF37]
                        font-semibold
                        hover:bg-[#D4AF37]/10
                        hover:border-[#D4AF37]
                        transition
                      "
                    >
                      + Add New Color
                    </button>

                  </div>

                  {/* Product Availability */}
                  <div className="mt-6">

                    <label className="block text-sm font-semibold text-gray-300 mb-2">
                      Product Availability
                    </label>

                    <div
                      className={`flex items-center justify-between border rounded-xl px-4 py-4 transition ${
                        formData.status
                          ? "border-[#D4AF37]/40 bg-[#D4AF37]/5"
                          : "border-red-500/30 bg-red-500/5"
                      }`}
                    >

                      <div>

                        <p
                          className={`font-semibold ${
                            formData.status
                              ? "text-[#D4AF37]"
                              : "text-red-400"
                          }`}
                        >
                          {formData.status
                            ? "Available"
                            : "Not Available"}
                        </p>

                        <p className="text-xs text-gray-500 mt-1">
                          {formData.status
                            ? "Customers can purchase this product."
                            : "Customers cannot purchase this product."}
                        </p>

                      </div>

                      <button
                        type="button"
                        onClick={() =>
                          setFormData({
                            ...formData,
                            status: formData.status
                              ? 0
                              : 1,
                          })
                        }
                        className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                          formData.status
                            ? "bg-[#D4AF37]"
                            : "bg-gray-700"
                        }`}
                        aria-label="Toggle product availability"
                      >

                        <span
                          className={`inline-block h-5 w-5 transform rounded-full shadow transition-transform ${
                            formData.status
                              ? "translate-x-6 bg-black"
                              : "translate-x-1 bg-white"
                          }`}
                        />

                      </button>

                    </div>

                  </div>

                </div>

                {/* =====================================================
                    FORM ACTIONS
                ===================================================== */}
                <div className="border-t border-gray-800 pt-6 flex flex-col-reverse sm:flex-row justify-end gap-3">

                  <button
                    type="button"
                    onClick={closeEditModal}
                    className="
                      px-7 py-3
                      border border-gray-700
                      rounded-xl
                      text-gray-300
                      font-semibold
                      hover:border-[#D4AF37]
                      hover:text-[#D4AF37]
                      transition
                    "
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    className="
                      px-8 py-3
                      bg-[#D4AF37]
                      text-black
                      rounded-xl
                      font-bold
                      hover:bg-[#b8941f]
                      transition
                      shadow-lg
                      shadow-[#D4AF37]/10
                    "
                  >
                    Update Product
                  </button>

                </div>

              </form>

            </div>

          </div>

        )}

        {/* =====================================================
            VIEW PRODUCT MODAL
        ===================================================== */}
        {viewProduct && (

          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-3 sm:p-4">

            <div className="bg-[#0b0b0b] border border-[#D4AF37]/40 text-white w-full max-w-2xl rounded-2xl sm:rounded-3xl shadow-2xl max-h-[90vh] overflow-hidden">

              {/* Header */}
              <div className="bg-black border-b border-[#D4AF37]/40 px-5 sm:px-6 py-5 flex items-center justify-between">

                <div>

                  <h3 className="text-xl font-bold text-white">
                    Product Details
                  </h3>

                  <p className="text-sm text-[#D4AF37] mt-1">
                    Product ID: #{viewProduct.id}
                  </p>

                </div>

                <button
                  type="button"
                  onClick={() =>
                    setViewProduct(null)
                  }
                  className="
                    w-10 h-10
                    rounded-xl
                    bg-gray-900
                    border border-gray-700
                    text-gray-300
                    hover:text-[#D4AF37]
                    hover:border-[#D4AF37]
                    transition
                  "
                >
                  ✕
                </button>

              </div>

              {/* Content */}
              <div className="p-5 sm:p-6 overflow-y-auto max-h-[calc(90vh-85px)]">

                {/* Product Image */}
                <div className="flex justify-center mb-6">

                  <div className="w-48 h-48 rounded-2xl overflow-hidden border border-[#D4AF37]/30 bg-black">

                    <img
                      src={
                        viewProduct.image
                          ? `${process.env.REACT_APP_API_URL}/public/${viewProduct.image}`
                          : "/placeholder.jpg"
                      }
                      alt={viewProduct.name}
                      className="w-full h-full object-cover"
                    />

                  </div>

                </div>

                {/* Name */}
                <h3 className="text-2xl font-bold text-white">
                  {viewProduct.name}
                </h3>

                {/* MRP */}
                <p className="text-sm text-gray-500 line-through mt-3">
                  MRP: ₹{viewProduct.mrp_price}
                </p>

                {/* Price */}
                <p className="text-2xl font-bold text-[#D4AF37] mt-1">
                  ₹{viewProduct.price}
                </p>

                {/* Basic Information */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">

                  {[
                    {
                      label: "Article Number",
                      value:
                        viewProduct.article_number ||
                        "-",
                    },
                    {
                      label: "HSN Code",
                      value:
                        viewProduct.hsn || "-",
                    },
                    {
                      label: "Quantity",
                      value:
                        viewProduct.qty ?? 0,
                    },
                    {
                      label: "Return Days",
                      value: `${
                        viewProduct.return_days ?? 0
                      } days`,
                    },
                  ].map((item) => (

                    <div
                      key={item.label}
                      className="
                        bg-black
                        border border-gray-800
                        rounded-xl
                        p-4
                      "
                    >

                      <p className="text-xs text-gray-500">
                        {item.label}
                      </p>

                      <p className="font-semibold text-gray-200 mt-1">
                        {item.value}
                      </p>

                    </div>

                  ))}

                </div>

                {/* Description */}
                {viewProduct.description && (

                  <div className="mt-6">

                    <h4 className="font-bold text-[#D4AF37] mb-2">
                      Description
                    </h4>

                    <div
                      dangerouslySetInnerHTML={{
                        __html:
                          viewProduct.description,
                      }}
                      className="text-gray-400 leading-relaxed"
                    />

                  </div>

                )}

                {/* Close */}
                <div className="flex justify-end mt-7">

                  <button
                    type="button"
                    onClick={() =>
                      setViewProduct(null)
                    }
                    className="
                      px-7 py-3
                      bg-[#D4AF37]
                      text-black
                      rounded-xl
                      font-bold
                      hover:bg-[#b8941f]
                      transition
                    "
                  >
                    Close
                  </button>

                </div>

              </div>

            </div>

          </div>

        )}

      </div>

    </div>
  );
};

export default ManageProducts;